import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AccountMenu } from './AccountMenu'
import { HelpMenu } from './HelpMenu'
import { HelpProvider, useHelp } from './HelpProvider'
import { AssumedSessionBanner } from './AssumedSessionBanner'
import { PrimaryNav } from './PrimaryNav'
import { ViewsPane } from './ViewsPane'
import { ShortcutProvider, useShortcutRegistry } from './Shortcuts'
import { SavedViewsProvider, useSavedViewRegistry } from './SavedViews'
import { Icon } from './Icon'
import { useAsync } from './Async'
import { coverage as coverageApi } from '../lib/api'
import { matchShortcut } from '../lib/shortcuts'
import { statusSegments } from '../lib/statusBar'
import { cachedRail, rememberRail, type RailChoice } from '../lib/preferences'
import { PRODUCT_ASSETS, PRODUCT_NAME } from '../lib/product'

/**
 * Organization slugs this deployment treats as not-production.
 *
 * A list rather than a flag because there is nowhere to put a flag yet: the tenant row carries a
 * `kind`, but it distinguishes the platform organization from a customer, not an environment. Naming
 * the slugs is honest about being a stopgap and is visible to the next person; a `NODE_ENV` check
 * would be wrong in the one case that matters, which is a production build pointed at staging.
 */
const NON_PRODUCTION_SLUGS = new Set(['development', 'staging', 'test', 'sandbox'])

/**
 * The frame every screen renders inside.
 *
 * This was `Masthead.tsx`, and the design it carried is the one thing here worth keeping unchanged:
 * one markup tree at every width, with the stylesheet choosing the layout. The component does not
 * know how wide the window is, so it cannot be wrong about it, and there is no second list of links
 * to drift out of step. The prototype uses a `ResizeObserver` only because it renders inside a
 * resizable preview frame; this application owns the viewport, and a media query is cheaper, simpler,
 * and does not re-render on every drag of a window edge.
 *
 * Three layouts rather than two now:
 *
 *   >= 1120px   76px labeled rail, 192px views pane, docked inspector
 *   700-1120px  76px labeled rail, no views pane, inspector as a sheet
 *   < 700px     56px bottom tab bar, no views pane, inspector as a sheet
 *
 * The rail collapses to 48px icons alone, which is the one piece of the shell's geometry an operator
 * chooses rather than the stylesheet. It is held here rather than in `PrimaryNav` because `--rail-w`
 * has to be set on an ancestor of `.shell-body` for the grid to read it, and `.shell` is that
 * ancestor. Below 700px there is no rail to collapse and the attribute means nothing - every rule
 * reading it is gated on `min-width: 700px`, which is what stops a desktop choice following somebody
 * onto their phone.
 *
 * The phone drawer is gone, and with it `data-testid="nav-toggle"`. Five destinations fit across a
 * phone with a readable label under each, so there is nothing left for a drawer to hold - which is
 * the whole reason the rail is five and not six (D-323). The account button inherits the test id's
 * job of being the one control that reveals further navigation, under the name `account-menu`.
 *
 * The assumed-tenant banner is its own grid row. It used to be `flex: 1 0 100%` with negative margins
 * hard-coded to the desktop masthead's padding - never adjusted in the phone block - and it now has to
 * clear both the rail and the bottom tabs. A row reaches both edges with no margin arithmetic at all,
 * and it stays undismissible for the reason it always was: forgetting you are inside somebody else's
 * organization is the failure this whole feature designs against.
 */
export function Shell(props: ShellProps) {
  /*
    The provider wraps the body rather than the body reading a module-level store, because the shell's
    one keydown listener has to see what the screen inside `<Outlet/>` registered - and that screen is
    a descendant of this component. A store outside React would work and would lose the unmount
    cleanup, which is the part that stops a keystroke acting on the screen somebody just left.
  */
  return (
    <ShortcutProvider>
      <SavedViewsProvider>
        {/* Inside the shortcut registry, because the keyboard shortcuts dialog it renders lists only
            the keys the screen on view registered. */}
        <HelpProvider roles={props.roles}>
          <ShellBody {...props} />
        </HelpProvider>
      </SavedViewsProvider>
    </ShortcutProvider>
  )
}

interface ShellProps {
  displayName: string
  roles: readonly string[]
  tenantName: string | null
  tenantSlug: string | null
  assumedByName?: string | null
  /** When the support session ends, so the banner can count down to it rather than just claim it will. */
  assumedExpiresUtc?: string | null
  onSignOut: () => void
  onReleaseTenant: () => void
  /** A support session was extended in place. The screen underneath does not move. */
  onExtended: () => void
}

function ShellBody({
  displayName,
  roles,
  tenantName,
  tenantSlug,
  assumedByName,
  assumedExpiresUtc,
  onSignOut,
  onReleaseTenant,
  onExtended
}: ShellProps) {
  const location = useLocation()
  const [query, setQuery] = useState('')

  // Read synchronously so the first render is already the rail the operator chose. Unlike the palette
  // this needs no pre-paint stamp: the root element is empty until the bundle mounts, so there is no
  // moment at which the wrong width is on screen.
  const [rail, setRail] = useState<RailChoice>(() => cachedRail())

  function toggleRail() {
    const next: RailChoice = rail === 'labeled' ? 'icons' : 'labeled'
    setRail(next)
    rememberRail(next)
  }

  // One read for the whole shell. Cheap, cacheable, and deliberately not a projection: the coverage
  // gap has to stay readable when a chart is stale, which is the entire reason it lives here rather
  // than on the Board.
  const fleet = useAsync(() => coverageApi.get(), [])

  const { handlers } = useShortcutRegistry()
  const { openHelp } = useHelp()

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const action = matchShortcut({
        key: event.key,
        metaKey: event.metaKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        shiftKey: event.shiftKey,
        target: event.target as { tagName?: string; isContentEditable?: boolean } | null
      })

      if (action === null) return

      // The command bar is the shell's own and works on every route, so it stays here rather than
      // being something each screen has to remember to register.
      if (action === 'command-bar') {
        event.preventDefault()
        document.querySelector<HTMLInputElement>('.query-bar input')?.focus()
        return
      }

      /*
        A dialog is a keystroke's owner while it is open.

        `Dialog` is the browser's own `<dialog>` in the top layer and handles Escape itself through
        `onCancel`. This listener is on the document and fires anyway, so without this guard Escape
        would close the revoke confirmation and the inspector behind it in one press - and the
        operator would be left wondering which of the two they had actually cancelled (D-447).
      */
      const target = event.target as Element | null
      if (target?.closest?.('dialog[open]')) return

      /*
        Help is the shell's own too, and asks the help provider directly rather than going through the
        registry. It used to find the top-bar button by its class name and click it, which made a
        keyboard shortcut depend on the stylesheet.

        The registry is the wrong home for it. One screen owns that set at a time and `register`
        replaces the whole thing, so a shell control registering there would be cleared by the next
        screen to mount - and `useShortcuts` clearing on unmount is the property that stops a stale
        handler acting on a screen somebody has left.

        Deliberately *below* the dialog guard above rather than beside the command bar. The command bar
        can sit there because its effect through an open dialog is to focus an input the browser has
        made inert, which is nothing happening; this one would put a second modal on top of the first,
        and an operator halfway through a revoke confirmation does not need a help sheet over it.
      */
      if (action === 'help') {
        event.preventDefault()
        openHelp()
        return
      }

      const handler = handlers[action]
      if (!handler) return

      event.preventDefault()
      handler()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [handlers, openHelp])

  // Whatever the screen on view published. Empty on every screen that publishes nothing, which is how
  // it read before - the difference is that it is now empty because there is nothing to say rather
  // than because there was no way to say it (D-450).
  const { views } = useSavedViewRegistry()

  const segments = statusSegments({
    coverage: fleet.value,
    rows: null,
    syncedUtc: fleet.value === null ? null : new Date()
  })

  const paneTitle = location.pathname.startsWith('/people') ? 'Segments' : 'Saved views'

  return (
    <div className="shell" data-rail={rail}>
      <header className="top-bar">
        <span className="brand">
          {/*
            The product's mark, beside the organization's name. It is the product's rather than the
            organization's because an MSP operator moves between customers in this same bar, and the
            one thing that should not change as they do is which console they are in. The
            organization's own mark is what the sign-in page shows (D-513).
          */}
          <img
            className="brand-mark"
            data-testid="brand-mark"
            src={PRODUCT_ASSETS.icon}
            // Named only when the text beside it is the organization's, so a screen reader hears
            // which console this is once and never "Coastline Coastline".
            alt={tenantName ? PRODUCT_NAME : ''}
            width={24}
            height={24}
          />
          <span className="brand-name" data-testid="brand-name">
            {tenantName ?? PRODUCT_NAME}
          </span>
        </span>

        <form
          className="query-bar"
          role="search"
          onSubmit={(event) => {
            event.preventDefault()
          }}
        >
          <Icon name="magnifying-glass" size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="field:value …"
            aria-label="Search"
          />
          <kbd aria-hidden="true">⌘K</kbd>
        </form>

        {/*
          Only when this is not production. Keyed on the organization's own slug rather than on the
          build mode: a production build pointed at a staging stack is exactly the case where somebody
          needs to be told, and `import.meta.env.DEV` is false there.
        */}
        {NON_PRODUCTION_SLUGS.has((tenantSlug ?? '').toLowerCase()) && (
          <span className="environment-badge">{tenantSlug}</span>
        )}

        {/*
          Beside the identity, last thing before it, at every width. Below 700px the top bar wraps and
          gives the query bar its own row, so this joins brand, badge and account on the first - a
          `flex: none` control there cannot push the page sideways, only make the bar taller, which is
          what `help.spec.ts` measures rather than assumes. The word "Help" is dropped there and the
          glyph keeps the same accessible name.
        */}
        <HelpMenu />

        <AccountMenu displayName={displayName} tenantName={tenantName} roles={roles} onSignOut={onSignOut} />
      </header>

      {assumedByName && (
        <AssumedSessionBanner
          tenantName={tenantName}
          assumedByName={assumedByName}
          expiresUtc={assumedExpiresUtc ?? null}
          onReleaseTenant={onReleaseTenant}
          onExtended={onExtended}
        />
      )}

      <div className="shell-body">
        <PrimaryNav roles={roles} rail={rail} onToggleRail={toggleRail} />
        <ViewsPane title={paneTitle} views={views} coverage={fleet.value} />
        <main>
          <Outlet />
        </main>
      </div>

      <div className="status-bar" role="status">
        {segments.map((segment) => (
          <span key={segment.key} className={segment.tone === 'problem' ? 'problem' : undefined}>
            {segment.text}
          </span>
        ))}
      </div>
    </div>
  )
}
